/**
 * Shared rendering helpers for owner-invoice line items (Owner Portal fix pass).
 *
 * The invoice engine emits one management-fee line per property plus per-task
 * vendor-upcharge lines, each carrying propertyId/propertyName. Invoices
 * generated before that change carry no property attribution at all. Every
 * renderer (PM preview, jsPDF download, owner portal, invoice email) groups
 * through this single helper so the legacy fallback lives in exactly one place.
 */

import type { OwnerInvoiceLineItem } from "./schema";

export interface OwnerInvoiceLineGroup {
  /** null = the ungrouped bucket ("Other charges", or the whole legacy invoice) */
  propertyId: number | null;
  propertyName: string | null;
  items: OwnerInvoiceLineItem[];
  subtotalCents: number;
}

export interface GroupedOwnerInvoiceLines {
  /**
   * false when NO line carries a propertyId (legacy invoice) — render the
   * single group as a flat list with no property headers or subtotals.
   */
  grouped: boolean;
  groups: OwnerInvoiceLineGroup[];
}

const TYPE_ORDER: Record<OwnerInvoiceLineItem["type"], number> = {
  management_fee: 0,
  vendor_upcharge: 1,
  adjustment: 2,
  other: 3,
};

export function groupOwnerInvoiceLineItems(items: OwnerInvoiceLineItem[]): GroupedOwnerInvoiceLines {
  const anyAttributed = items.some((li) => li.propertyId != null);
  if (!anyAttributed) {
    return {
      grouped: false,
      groups: [
        {
          propertyId: null,
          propertyName: null,
          items,
          subtotalCents: items.reduce((sum, li) => sum + li.amountCents, 0),
        },
      ],
    };
  }

  // Group by propertyId in first-seen order; unattributed lines trail in an
  // "Other charges" bucket (propertyName null).
  const byProperty = new Map<number, OwnerInvoiceLineGroup>();
  const other: OwnerInvoiceLineItem[] = [];
  for (const li of items) {
    if (li.propertyId == null) {
      other.push(li);
      continue;
    }
    let group = byProperty.get(li.propertyId);
    if (!group) {
      group = { propertyId: li.propertyId, propertyName: li.propertyName ?? null, items: [], subtotalCents: 0 };
      byProperty.set(li.propertyId, group);
    }
    group.items.push(li);
    group.subtotalCents += li.amountCents;
    if (group.propertyName == null && li.propertyName != null) group.propertyName = li.propertyName;
  }

  const groups = Array.from(byProperty.values());
  for (const group of groups) {
    group.items = [...group.items].sort((a, b) => TYPE_ORDER[a.type] - TYPE_ORDER[b.type]);
  }
  if (other.length > 0) {
    groups.push({
      propertyId: null,
      propertyName: null,
      items: other,
      subtotalCents: other.reduce((sum, li) => sum + li.amountCents, 0),
    });
  }

  return { grouped: true, groups };
}
